
import React, { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { BrandAwareCard as Card, BrandAwareCardContent as CardContent, BrandAwareCardDescription as CardDescription, BrandAwareCardHeader as CardHeader, BrandAwareCardTitle as CardTitle } from "@/components/brand/BrandAwareCard"
import { Badge } from "@/components/ui/badge"
import { BrandAwareButton as Button } from "@/components/brand/BrandAwareButton"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Building, Users, Mail, Linkedin, DollarSign, TrendingUp, Globe, Target, ArrowUp, ArrowDown, Flag, Zap, CheckCircle, AlertCircle, XCircle, BarChart3, Phone, Calendar as CalendarIcon, MessageCircle, ExternalLink, FileSpreadsheet, FileText, Download, HelpCircle, Shield, Settings, Server, CalendarDays, Tags } from 'lucide-react'
import { format } from "date-fns"
import { cn } from "@/lib/utils"
import { useClients } from '@/hooks/useClients'
import { useMailboxes } from '@/hooks/useMailboxes'
import { useLinkedInProfiles } from '@/hooks/useLinkedInProfiles'
import { useConversionFunnel } from '@/hooks/useConversionFunnel'
import { isPrimaryDomain } from '@/lib/primary-domains'
import { isHealthyMailbox } from '@/lib/client-domain-categorizer'
import { ProfileDebug } from '@/components/debug/ProfileDebug'
import { EmailsSentDetailView } from './EmailsSentDetailView'
import { TotalRepliesDetailView } from './TotalRepliesDetailView'
import { PositiveRepliesDetailView } from './PositiveRepliesDetailView'
import { HumanRepliesDetailView } from './HumanRepliesDetailView'
import { NewMeetingsBookedDetailView } from './NewMeetingsBookedDetailView'

import { QualifiedMeetingsDetailView } from './QualifiedMeetingsDetailView'
import { DealsClosedDetailView } from './DealsClosedDetailView'
import { CollapsibleSection } from './CollapsibleSection'
import { TrendIndicator } from './TrendIndicator'
import { ManualAnalyticsFetch } from '@/components/debug/ManualAnalyticsFetch'
import { MaxLeadsFetch } from '@/components/debug/MaxLeadsFetch'

import { useDashboard } from '@/contexts/DashboardContext'
import { useWorkspaceClient } from '@/hooks/useWorkspaceClient'
import { useDailyInfraUsage } from '@/hooks/useDailyInfraUsage'
import { LiveCampaignsSection } from './LiveCampaignsSection'

export function ComprehensiveDashboard() {
  const { data: clients = [] } = useClients()
  const workspace = useWorkspaceClient()
  const { data: mailboxes = [] } = useMailboxes(workspace.mailboxClientId)
  // linkedin_profiles.client_id stores SmartLead IDs as strings (e.g. "167887"),
  // so pass mailboxClientId (SmartLead integer) converted to string
  const linkedInClientFilter = workspace.mailboxClientId?.toString()
  const { data: linkedinProfiles = [] } = useLinkedInProfiles(linkedInClientFilter)
  const { data: dailyUsage } = useDailyInfraUsage(workspace.mailboxClientId)
  const { state } = useDashboard()
  const [timeFrame, setTimeFrame] = useState('MTD')
  const [customDateRange, setCustomDateRange] = useState<{from: Date | undefined; to: Date | undefined}>({from: undefined, to: undefined})
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false)
  const [activeDetailView, setActiveDetailView] = useState<string | null>(null)
  
  
  // Fetch real conversion funnel data
  // For client users: use workspace-resolved ID to avoid race condition with DashboardContext
  // For admins: use state.selectedClient ('all' = all active clients, or specific client)
  const getClientFilterForFunnel = () => {
    if (workspace.isClientUser) {
      return workspace.campaignClientId || undefined
    }
    return state.selectedClient
  }

  const { data: conversionData, isLoading: isLoadingConversion } = useConversionFunnel({
    timeFrame,
    clientId: getClientFilterForFunnel(),
    dateRange: customDateRange,
  })

  // Debug conversion data to understand what we're getting
  useEffect(() => {
    if (conversionData) {
      console.log('🔍 CONVERSION DATA DEBUG:', {
        totalReplies: conversionData.totalReplies,
        humanReplies: conversionData.humanReplies,
        positiveReplies: conversionData.positiveReplies,
        emailsSent: conversionData.emailsSent,
        timeFrame,
        clientId: getClientFilterForFunnel()
      })
    }
  }, [conversionData, timeFrame])
  const handleDateRangeChange = (range: any) => {
    setCustomDateRange(range || {from: undefined, to: undefined})
  }
  
  // Use the selected client from the dashboard context
  const selectedClientId = state.selectedClient


  // Data is already DB-filtered by useMailboxes(workspace.mailboxClientId) and
  // useLinkedInProfiles(workspace.campaignClientId). No additional client-side filtering needed
  // when workspace is scoped. For admin "all" view, data is unfiltered at DB level.
  const filteredMailboxes = mailboxes
  const filteredLinkedInProfiles = linkedinProfiles

  // Real-time Infrastructure Calculations
  // Primary = exact business domain (e.g. cruva.com, yotpo.com) — uses PRIMARY_DOMAINS list
  // Secondary = all other outreach/lookalike domains (e.g. cruvaapp.com, getcruva.com)
  const filteredPrimaryMailboxes = filteredMailboxes.filter(mailbox =>
    isPrimaryDomain(mailbox.email)
  )

  const filteredSecondaryMailboxes = filteredMailboxes.filter(mailbox =>
    !isPrimaryDomain(mailbox.email)
  )

  // Calculate healthy mailboxes
  const healthyMailboxes = filteredMailboxes.filter(mailbox => isHealthyMailbox(mailbox))
  
  // Use filtered mailbox data for email utilization calculations
  const activeSmartleadMailboxes = filteredMailboxes.filter(m => 
    (m.status === 'ACTIVE' || m.status === 'Active') && m.smartlead_id
  )
  
  const totalEmailCapacity = activeSmartleadMailboxes.reduce((sum, m) => sum + (m.daily_limit || 0), 0)
  const currentEmailUsage = dailyUsage?.emailSentToday ?? 0
  const emailUtilization = totalEmailCapacity > 0 ? Math.round((currentEmailUsage / totalEmailCapacity) * 100) : 0

  // Calculate primary mailbox metrics from FILTERED data
  const activePrimaryMailboxes = filteredPrimaryMailboxes.filter(m => 
    (m.status === 'ACTIVE' || m.status === 'Active') && m.smartlead_id
  )
  const primaryCapacity = activePrimaryMailboxes.reduce((sum, m) => sum + (m.daily_limit || 0), 0)
  const primaryUsage = activePrimaryMailboxes.reduce((sum, m) => sum + (m.current_daily_sent || 0), 0)
  const primaryUtilization = primaryCapacity > 0 ? Math.round((primaryUsage / primaryCapacity) * 100) : 0

  // Calculate secondary mailbox metrics from FILTERED data
  const activeSecondaryMailboxes = filteredSecondaryMailboxes.filter(m => 
    (m.status === 'ACTIVE' || m.status === 'Active') && m.smartlead_id
  )
  const secondaryCapacity = activeSecondaryMailboxes.reduce((sum, m) => sum + (m.daily_limit || 0), 0)
  const secondaryUsage = activeSecondaryMailboxes.reduce((sum, m) => sum + (m.current_daily_sent || 0), 0)
  const secondaryUtilization = secondaryCapacity > 0 ? Math.round((secondaryUsage / secondaryCapacity) * 100) : 0

  // LinkedIn calculations — daily usage from heyreach_linkedin_events, not cumulative profile totals
  const totalLinkedInCapacity = filteredLinkedInProfiles.reduce((sum, p) => sum + (p.daily_direct_messages || 0), 0)
  const currentLinkedInUsage = dailyUsage?.linkedinSentToday ?? 0
  const linkedinUtilization = totalLinkedInCapacity > 0 ? Math.round((currentLinkedInUsage / totalLinkedInCapacity) * 100) : 0

  // E-commerce Industry Analysis Data
  const industryBreakdown = [
    { segment: 'Fashion & Apparel', sends: '8.2k', replies: 156, rate: '1.9%', positive: 28, meetings: 8, pipeline: '$42k' },
    { segment: 'Beauty & Cosmetics', sends: '6.8k', replies: 128, rate: '1.8%', positive: 24, meetings: 6, pipeline: '$36k' },
    { segment: 'Home & Garden', sends: '5.9k', replies: 94, rate: '1.6%', positive: 18, meetings: 4, pipeline: '$28k' },
    { segment: 'Health & Fitness', sends: '5.2k', replies: 82, rate: '1.6%', positive: 15, meetings: 3, pipeline: '$22k' },
    { segment: 'Electronics & Tech', sends: '4.8k', replies: 67, rate: '1.4%', positive: 12, meetings: 2, pipeline: '$18k' },
    { segment: 'Food & Beverage', sends: '3.9k', replies: 43, rate: '1.1%', positive: 8, meetings: 1, pipeline: '$12k' },
  ]

  const trafficAnalysis = [
    { volume: '1M+ visitors/mo', companies: 52, replyRate: '3.2%', meetings: 18, pipeline: '$84k' },
    { volume: '500K-1M visitors/mo', companies: 89, replyRate: '2.8%', meetings: 12, pipeline: '$58k' },
    { volume: '250K-500K visitors/mo', companies: 124, replyRate: '2.1%', meetings: 8, pipeline: '$42k' },
    { volume: '100K-250K visitors/mo', companies: 186, replyRate: '1.8%', meetings: 6, pipeline: '$28k' },
    { volume: '50K-100K visitors/mo', companies: 248, replyRate: '1.4%', meetings: 4, pipeline: '$18k' },
    { volume: '10K-50K visitors/mo', companies: 392, replyRate: '1.1%', meetings: 2, pipeline: '$12k' },
    { volume: '1K-10K visitors/mo', companies: 512, replyRate: '0.8%', meetings: 1, pipeline: '$6k' },
    { volume: 'Under 1K visitors/mo', companies: 284, replyRate: '0.5%', meetings: 0, pipeline: '$0' },
  ]

  const employeeAnalysis = [
    { size: '1000+ employees', companies: 38, replyRate: '2.9%', meetings: 14, pipeline: '$72k' },
    { size: '500-999 employees', companies: 67, replyRate: '2.4%', meetings: 9, pipeline: '$48k' },
    { size: '200-499 employees', companies: 124, replyRate: '1.9%', meetings: 7, pipeline: '$36k' },
    { size: '100-199 employees', companies: 196, replyRate: '1.6%', meetings: 5, pipeline: '$24k' },
    { size: '50-99 employees', companies: 285, replyRate: '1.3%', meetings: 3, pipeline: '$18k' },
    { size: '20-49 employees', companies: 342, replyRate: '1.0%', meetings: 2, pipeline: '$12k' },
    { size: '10-19 employees', companies: 418, replyRate: '0.8%', meetings: 1, pipeline: '$6k' },
    { size: '1-9 employees', companies: 617, replyRate: '0.4%', meetings: 0, pipeline: '$0' },
  ]

  const technologyStack = [
    { platform: 'Shopify', companies: 584, replyRate: '2.1%', avgRevenue: '$8.2M', meetings: 23, pipeline: '$96k', useCases: 'Reviews, Loyalty' },
    { platform: 'WooCommerce', companies: 392, replyRate: '1.4%', avgRevenue: '$3.1M', meetings: 12, pipeline: '$48k', useCases: 'Email, SMS' },
    { platform: 'Magento', companies: 218, replyRate: '1.9%', avgRevenue: '$12.4M', meetings: 8, pipeline: '$36k', useCases: 'Reviews, Email' },
    { platform: 'BigCommerce', companies: 164, replyRate: '1.6%', avgRevenue: '$6.8M', meetings: 6, pipeline: '$24k', useCases: 'Loyalty, SMS' },
    { platform: 'Squarespace', companies: 128, replyRate: '0.9%', avgRevenue: '$1.8M', meetings: 3, pipeline: '$12k', useCases: 'Email, Reviews' },
    { platform: 'Custom/Other', companies: 241, replyRate: '0.7%', avgRevenue: '$4.2M', meetings: 4, pipeline: '$18k', useCases: 'Various' },
  ]

  // Product Line Performance Data
  const productCards = [
    { name: 'Reviews & UGC', amount: '$36k', status: 'Top Revenue Generator', change: '+89% vs last month', color: 'bg-emerald-100 border-emerald-300', textColor: 'text-emerald-600' },
    { name: 'Loyalty & Referrals', amount: '$12k', status: 'Steady Performer', change: '+5% vs last month', color: 'bg-amber-100 border-amber-300', textColor: 'text-amber-600' },
    { name: 'Emails & SMS', amount: '$0', status: 'Needs Attention', change: '-100% vs last month', color: 'bg-red-100 border-red-300', textColor: 'text-red-600' },
  ]

  const productLineDetails = [
    { line: 'Reviews & UGC', description: 'User-generated content & review management', positiveReplies: 156, meetings: 7, qualified: 6, dealsClosed: 2, avgAcv: '$18k', totalRevenue: '$36k', pipeline: '$72k', momGrowth: '+89%' },
    { line: 'Loyalty & Referrals', description: 'Customer retention & referral programs', positiveReplies: 128, meetings: 3, qualified: 2, dealsClosed: 1, avgAcv: '$12k', totalRevenue: '$12k', pipeline: '$24k', momGrowth: '+5%' },
    { line: 'Emails & SMS', description: 'Marketing automation & messaging', positiveReplies: 102, meetings: 2, qualified: 1, dealsClosed: 0, avgAcv: '-', totalRevenue: '$0', pipeline: '$15k', momGrowth: '-100%' },
  ]

  // Team Geography Performance Data
  const teamPerformance = [
    { 
      country: 'Australia Team', 
      flag: '🇦🇺', 
      sendVolume: '14.2k', 
      replyVolume: 270, 
      positiveReplies: 68, 
      meetingsBooked: 4, 
      qualifiedMeetings: 3, 
      pipelineCreated: '$60k', 
      dealsClosed: 1, 
      revenueGenerated: '$18k' 
    },
    { 
      country: 'USA Team', 
      flag: '🇺🇸', 
      sendVolume: '64.8k', 
      replyVolume: 713, 
      positiveReplies: 158, 
      meetingsBooked: 4, 
      qualifiedMeetings: 3, 
      pipelineCreated: '$60k', 
      dealsClosed: 1, 
      revenueGenerated: '$12k' 
    },
    { 
      country: 'France Team', 
      flag: '🇫🇷', 
      sendVolume: '28.1k', 
      replyVolume: 197, 
      positiveReplies: 52, 
      meetingsBooked: 2, 
      qualifiedMeetings: 2, 
      pipelineCreated: '$30k', 
      dealsClosed: 0, 
      revenueGenerated: '$0' 
    },
    { 
      country: 'UK/EMEA Team', 
      flag: '🇬🇧', 
      sendVolume: '42.9k', 
      replyVolume: 300, 
      positiveReplies: 108, 
      meetingsBooked: 2, 
      qualifiedMeetings: 1, 
      pipelineCreated: '$30k', 
      dealsClosed: 1, 
      revenueGenerated: '$18k' 
    },
  ]

  // Handle detail view navigation
  const handleFunnelClick = (stage: string) => {
    if (stage === 'Emails Sent' || stage === 'Total Replies' || stage === 'Human Replies' || stage === 'Positive Replies' || stage === 'Meetings Booked' || stage === 'Deals Closed') {
      setActiveDetailView(stage)
    } else if (stage === 'Qualified Meetings') {
      // Redirect qualified meetings to unified meetings booked view
      setActiveDetailView('Meetings Booked')
    }
  }

  const handleBackToMain = () => {
    setActiveDetailView(null)
  }

  // Show detail view if one is active
  if (activeDetailView === 'Emails Sent') {
    return <EmailsSentDetailView 
      onBack={handleBackToMain} 
      clientFilter={getClientFilterForFunnel()}
      timeFrame={timeFrame}
      customDateRange={customDateRange}
      onTimeFrameChange={setTimeFrame}
      onCustomDateRangeChange={(dateRange) => {
        setCustomDateRange({
          from: dateRange?.from,
          to: dateRange?.to
        })
      }}
    />
  }
  if (activeDetailView === 'Total Replies') {
    return <TotalRepliesDetailView 
      onBack={handleBackToMain} 
      clientFilter={getClientFilterForFunnel()}
      timeFrame={timeFrame}
      customDateRange={customDateRange}
      onTimeFrameChange={setTimeFrame}
      onCustomDateRangeChange={(dateRange) => {
        setCustomDateRange({
          from: dateRange?.from,
          to: dateRange?.to
        })
      }}
    />
  }
  if (activeDetailView === 'Human Replies') {
    return <HumanRepliesDetailView 
      onBack={handleBackToMain} 
      clientFilter={getClientFilterForFunnel()}
      timeFrame={timeFrame}
      customDateRange={customDateRange}
      onTimeFrameChange={setTimeFrame}
      onCustomDateRangeChange={(dateRange) => {
        setCustomDateRange({
          from: dateRange?.from,
          to: dateRange?.to
        })
      }}
    />
  }
  if (activeDetailView === 'Positive Replies') {
    return <PositiveRepliesDetailView 
      onBack={handleBackToMain} 
      clientFilter={getClientFilterForFunnel()}
      timeFrame={timeFrame}
      customDateRange={customDateRange}
      onTimeFrameChange={setTimeFrame}
      onCustomDateRangeChange={(dateRange) => {
        setCustomDateRange({
          from: dateRange?.from,
          to: dateRange?.to
        })
      }}
    />
  }
  if (activeDetailView === 'Meetings Booked') {
    return <NewMeetingsBookedDetailView 
      onBack={handleBackToMain} 
      clientFilter={getClientFilterForFunnel()}
      timeFrame={timeFrame}
      customDateRange={
        customDateRange.from && customDateRange.to 
          ? { from: customDateRange.from, to: customDateRange.to }
          : undefined
      }
      onTimeFrameChange={setTimeFrame}
      onCustomDateRangeChange={(dateRange) => {
        setCustomDateRange({
          from: dateRange?.from,
          to: dateRange?.to
        })
      }}
    />
  }
  if (activeDetailView === 'Deals Closed') {
    return <DealsClosedDetailView 
      onBack={handleBackToMain} 
      clientFilter={getClientFilterForFunnel()}
      timeFrame={timeFrame}
      customDateRange={customDateRange}
      onTimeFrameChange={setTimeFrame}
      onCustomDateRangeChange={(dateRange) => {
        setCustomDateRange({
          from: dateRange?.from,
          to: dateRange?.to
        })
      }}
    />
  }
  
  // Conversion Funnel Data - Real-time data based on selected timeframe
  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k'
    return num.toString()
  }

  const formatCurrency = (num: number) => {
    if (num >= 1000000) return '$' + (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return '$' + (num / 1000).toFixed(0) + 'k'
    return '$' + num.toString()
  }

  // Conversion funnel: only show stages with real data (Meetings/Qualified/Deals hidden until data pipeline is ready)
  const conversionFunnel = isLoadingConversion ? [
    { stage: 'Emails Sent', value: 'Loading...', subtitle: 'Loading...', detail: 'Loading...', growth: '', color: 'bg-blue-500' },
    { stage: 'Total Replies', value: 'Loading...', subtitle: 'Loading...', detail: 'Loading...', growth: '', color: 'bg-emerald-500' },
    { stage: 'Human Replies', value: 'Loading...', subtitle: 'Loading...', detail: 'Loading...', growth: '', color: 'bg-teal-500' },
    { stage: 'Positive Replies', value: 'Loading...', subtitle: 'Loading...', detail: 'Loading...', growth: '', color: 'bg-amber-500' },
  ] : [
    {
      stage: 'Emails Sent',
      value: formatNumber(conversionData?.emailsSent || 0),
      subtitle: `${formatNumber(conversionData?.leadsContacted || 0)} leads contacted`,
      detail: '',
      growth: '',
      color: 'bg-blue-500'
    },
    {
      stage: 'Total Replies',
      value: formatNumber(conversionData?.totalReplies || 0),
      subtitle: `${conversionData?.totalReplies && conversionData?.leadsContacted ? ((conversionData.totalReplies / conversionData.leadsContacted) * 100).toFixed(1) : '0.0'}% of leads`,
      detail: '',
      growth: '',
      color: 'bg-emerald-500'
    },
    {
      stage: 'Human Replies',
      value: formatNumber(conversionData?.humanReplies || 0),
      subtitle: `${((conversionData?.humanReplies || 0) / (conversionData?.totalReplies || 1) * 100).toFixed(1)}% of replies`,
      detail: '',
      growth: '',
      color: 'bg-teal-500'
    },
    {
      stage: 'Positive Replies',
      value: formatNumber(conversionData?.positiveReplies || 0),
      subtitle: `${((conversionData?.positiveReplies || 0) / (conversionData?.humanReplies || 1) * 100).toFixed(1)}% of human`,
      detail: '',
      growth: '',
      color: 'bg-amber-500'
    },
  ]

  // Time frame options
  const timeFrameOptions = [
    { value: 'MTD', label: 'MTD' },
    { value: 'LAST_MONTH', label: 'Last month' },
    { value: 'LAST_QUARTER', label: 'Last Quarter' },
    { value: 'LAST_6_MONTHS', label: 'Last 6 months' },
    { value: 'LAST_1_YEAR', label: 'Last 1 year' },
    { value: 'CUSTOM', label: 'Custom' },
  ]

  const getTimeFrameLabel = () => {
    if (timeFrame === 'CUSTOM') {
      if (customDateRange.from && customDateRange.to) {
        return `${format(customDateRange.from, 'MMM dd')} - ${format(customDateRange.to, 'MMM dd, yyyy')} Performance`
      }
      return 'Custom Range Performance'
    }
    
    // Map time frame values to proper labels with "Performance"
    const timeFrameLabels = {
      'MTD': 'Month to Date Performance',
      'LAST_MONTH': 'Last Month Performance', 
      'LAST_QUARTER': 'Last Quarter Performance',
      'LAST_6_MONTHS': 'Last 6 Months Performance',
      'LAST_1_YEAR': 'Last 1 Year Performance'
    }
    
    return timeFrameLabels[timeFrame] || 'Current Performance'
  }

  return (
    <div className="space-y-6 p-6 min-h-screen">
      {/* Header — compact for client users, full for admins */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {workspace.isClientUser ? 'Dashboard' : 'Performance Analytics'}
          </h1>
          {!workspace.isClientUser && (
            <p className="text-sm text-muted-foreground mt-0.5">{getTimeFrameLabel()}</p>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Time Frame Filter */}
          <div className="flex items-center gap-2 border rounded-lg px-3 py-1.5">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <Select value={timeFrame} onValueChange={setTimeFrame}>
              <SelectTrigger className="w-36 h-7 border-none shadow-none focus:ring-0 p-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border shadow-lg z-50">
                {timeFrameOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {timeFrame === 'CUSTOM' && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "w-56 justify-start text-left font-normal h-7",
                      !customDateRange.from && !customDateRange.to && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-3 w-3" />
                    {customDateRange.from ? (
                      customDateRange.to ? (
                        <>
                          {format(customDateRange.from, "LLL dd, y")} -{" "}
                          {format(customDateRange.to, "LLL dd, y")}
                        </>
                      ) : (
                        format(customDateRange.from, "LLL dd, y")
                      )
                    ) : (
                      <span>Pick a date range</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 z-50" align="start">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={customDateRange.from}
                    selected={customDateRange}
                    onSelect={handleDateRangeChange}
                    numberOfMonths={2}
                    className="p-3 pointer-events-auto relative z-50"
                  />
                </PopoverContent>
              </Popover>
            )}
          </div>

          {/* Client Filter — only for admin users */}
          {!workspace.isClientUser && (
            <div className="flex items-center gap-2 border rounded-lg px-3 py-1.5 text-sm">
              <span className="text-muted-foreground">Client:</span>
              <span className="font-medium">
                {selectedClientId === 'all' ? 'All' :
                 clients.find(c => c.id === selectedClientId)?.name || selectedClientId}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Performance Overview */}
      <section id="funnel" className="scroll-mt-8">
        <Card>
        <CardContent className="px-6 py-6">
          <div className="flex items-center justify-center gap-6">
              {conversionFunnel.map((stage, index) => (
                <div key={index} className="flex items-center gap-6">
                  <div className="flex flex-col items-center min-w-[120px]">
                    <div
                      className={`w-24 h-24 rounded-full ${stage.color} flex items-center justify-center text-white font-bold text-xl mb-2 cursor-pointer hover:opacity-80 hover:scale-105 transition-all shadow-md`}
                      onClick={() => handleFunnelClick(stage.stage)}
                    >
                      {stage.value}
                    </div>
                   <div className="text-center">
                     <div className="font-semibold text-sm text-foreground">{stage.stage}</div>
                     <div className="text-xs text-muted-foreground mt-0.5">{typeof stage.subtitle === 'string' ? stage.subtitle : stage.subtitle}</div>
                     {stage.growth && (
                       <TrendIndicator
                         value={stage.growth}
                         trend={stage.growth.includes('+') ? 'up' : stage.growth.includes('-') ? 'down' : 'stable'}
                         className="mt-1"
                       />
                     )}
                   </div>
                 </div>
                 {index < conversionFunnel.length - 1 && (
                   <ArrowUp className="h-5 w-5 text-slate-300 rotate-90 flex-shrink-0" />
                 )}
               </div>
             ))}
          </div>
        </CardContent>
      </Card>
      </section>


      {/* Live Campaigns Section */}
      <LiveCampaignsSection selectedClientId={selectedClientId} />

      {/* Infrastructure Capacity & Usage */}
      <section id="infrastructure" className="scroll-mt-8">
        <div className="flex items-center gap-2 mb-4">
          <Server className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Infrastructure Capacity</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Email Infrastructure */}
          <Card className="overflow-hidden">
            <div className="flex items-center gap-2 px-5 pt-4 pb-2">
              <Mail className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-semibold text-foreground">Email</span>
            </div>
            <CardContent className="px-5 pb-5 pt-0">
              {/* Capacity bar */}
              <div className="mb-4">
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-2xl font-bold text-foreground">{currentEmailUsage.toLocaleString()}<span className="text-sm font-normal text-muted-foreground">/{totalEmailCapacity.toLocaleString()}</span></span>
                  <span className="text-sm font-medium text-muted-foreground">{emailUtilization}% used</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${emailUtilization > 80 ? 'bg-red-500' : emailUtilization > 50 ? 'bg-amber-500' : 'bg-blue-500'}`}
                    style={{ width: `${Math.min(emailUtilization, 100)}%` }}
                  />
                </div>
                <div className="text-xs text-muted-foreground mt-1">Daily send capacity</div>
              </div>

              {/* Mailbox breakdown */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-slate-50 px-3 py-2.5">
                  <div className="text-xl font-bold text-foreground">{filteredPrimaryMailboxes.length}</div>
                  <div className="text-xs text-muted-foreground">Primary mailboxes</div>
                </div>
                <div className="rounded-lg bg-slate-50 px-3 py-2.5">
                  <div className="text-xl font-bold text-foreground">{filteredSecondaryMailboxes.length}</div>
                  <div className="text-xs text-muted-foreground">Secondary mailboxes</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* LinkedIn Infrastructure */}
          <Card className="overflow-hidden">
            <div className="flex items-center gap-2 px-5 pt-4 pb-2">
              <Linkedin className="h-4 w-4 text-violet-600" />
              <span className="text-sm font-semibold text-foreground">LinkedIn</span>
            </div>
            <CardContent className="px-5 pb-5 pt-0">
              {/* Capacity bar */}
              <div className="mb-4">
                <div className="flex items-baseline justify-between mb-1.5">
                  <span className="text-2xl font-bold text-foreground">{currentLinkedInUsage.toLocaleString()}<span className="text-sm font-normal text-muted-foreground">/{totalLinkedInCapacity.toLocaleString()}</span></span>
                  <span className="text-sm font-medium text-muted-foreground">{linkedinUtilization}% used</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${linkedinUtilization > 80 ? 'bg-red-500' : linkedinUtilization > 50 ? 'bg-amber-500' : 'bg-violet-500'}`}
                    style={{ width: `${Math.min(linkedinUtilization, 100)}%` }}
                  />
                </div>
                <div className="text-xs text-muted-foreground mt-1">Daily message capacity</div>
              </div>

              {/* Account breakdown */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg bg-slate-50 px-3 py-2.5">
                  <div className="text-xl font-bold text-foreground">{filteredLinkedInProfiles.filter(p => p.platform_status === 'active').length}<span className="text-sm font-normal text-muted-foreground">/{filteredLinkedInProfiles.length}</span></div>
                  <div className="text-xs text-muted-foreground">Active accounts</div>
                </div>
                <div className="rounded-lg bg-slate-50 px-3 py-2.5">
                  <div className="text-xl font-bold text-foreground">{totalLinkedInCapacity > 0 ? Math.round(totalLinkedInCapacity / Math.max(filteredLinkedInProfiles.filter(p => p.platform_status === 'active').length, 1)) : 0}</div>
                  <div className="text-xs text-muted-foreground">Messages/account/day</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Quick Actions Section — admin only (contains internal tools like SmartLead, Blocklist) */}
      {!workspace.isClientUser && <Card>
        <CardHeader>
          <CardTitle className="text-xl font-semibold flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Key Information & Quick Actions
          </CardTitle>
          <CardDescription className="text-slate-600">
            Essential tools and resources for client management and performance tracking
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Communication */}
            <div className="rounded-lg p-4 border bg-card hover:bg-accent/50 transition-colors">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Communication
              </h3>
              <div className="space-y-2">
                <Button variant="outline" size="sm" className="w-full justify-start text-xs h-8">
                  <CalendarIcon className="h-3 w-3 mr-2" />
                  Schedule Call
                </Button>
                <Button variant="outline" size="sm" className="w-full justify-start text-xs h-8">
                  <MessageCircle className="h-3 w-3 mr-2" />
                  Join Slack Channel
                </Button>
              </div>
            </div>

            {/* Data & Analytics */}
            <div className="rounded-lg p-4 border bg-card hover:bg-accent/50 transition-colors">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Data & Analytics
              </h3>
              <div className="space-y-2">
                <Button variant="outline" size="sm" className="w-full justify-start text-xs h-8">
                  <ExternalLink className="h-3 w-3 mr-2" />
                  SmartLead Instance
                </Button>
                <Button variant="outline" size="sm" className="w-full justify-start text-xs h-8">
                  <FileSpreadsheet className="h-3 w-3 mr-2" />
                  Reply Data Sheet
                </Button>
              </div>
            </div>

            {/* Reports & Export */}
            <div className="rounded-lg p-4 border bg-card hover:bg-accent/50 transition-colors">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Reports & Export
              </h3>
              <div className="space-y-2">
                <Button variant="outline" size="sm" className="w-full justify-start text-xs h-8">
                  <Download className="h-3 w-3 mr-2" />
                  Download PDF
                </Button>
                <Button variant="outline" size="sm" className="w-full justify-start text-xs h-8">
                  <TrendingUp className="h-3 w-3 mr-2" />
                  Board Summary
                </Button>
              </div>
            </div>

            {/* Support & Tools */}
            <div className="rounded-lg p-4 border bg-card hover:bg-accent/50 transition-colors">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <HelpCircle className="h-4 w-4" />
                Support & Tools
              </h3>
              <div className="space-y-2">
                <Button variant="outline" size="sm" className="w-full justify-start text-xs h-8">
                  <Shield className="h-3 w-3 mr-2" />
                  View Blocklist
                </Button>
                <Button variant="outline" size="sm" className="w-full justify-start text-xs h-8">
                  <Settings className="h-3 w-3 mr-2" />
                  Help & Tutorials
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>}

      {/* Debug Panel — admin only */}
      {!workspace.isClientUser && (
        <section className="mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
            <ProfileDebug />
          </div>
        </section>
      )}

    </div>
  )
}
